import axios from 'axios';
import { BACKEND_URL } from './backendUrl';

const API_BASE_URL = BACKEND_URL + '/api';

// Send & receive httpOnly auth cookie on EVERY axios call (covers both this
// shared `api` instance and any raw `import axios from 'axios'` usage).
axios.defaults.withCredentials = true;

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Bearer-token fallback for cross-origin contexts ──────────────────────────
// In a regular browser the httpOnly cookie set by /auth/login carries the
// session on every same-origin call. But inside Capacitor's Android WebView
// the React bundle is served from `https://localhost` while the API lives at
// `crm.zartecsolutions.com` — that's a cross-origin request, and most
// browsers (including the Android WebView) will silently drop httpOnly
// cookies in that situation.
//
// Backend `auth._extract_token` already accepts `Authorization: Bearer
// <jwt>` as a fallback. So we mirror the cookie into localStorage on login
// (see AuthContext.js) and re-attach it as a header on every request here.
// On the web app the cookie still authenticates and the bearer header is a
// harmless duplicate. In the APK the bearer header is the *only* thing
// keeping the user logged in.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      const url = error.config?.url || '';
      const path = window.location.pathname || '';
      // Don't redirect if:
      //  - the failing call is the bootstrap /auth/me probe
      //  - we're already on /login or /register (would cause infinite loops)
      const onPublicPage = path === '/login' || path === '/register';
      if (!url.endsWith('/auth/me') && !onPublicPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
